import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const HEARTBEAT_MS = 30000;
const LIVE_PHASES = ['ready', 'work', 'rest', 'summary'];

const send = (method, body) =>
  fetch(`${API_URL}/api/client/live-workout`, {
    method,
    credentials: 'include',
    keepalive: true, // still delivered when the page is being closed
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  }).catch(() => {}); // best effort: a missed update only delays the trainer's view

// Tells the trainer's dashboard what the client is doing in the workout player
// right now: on every step (start, set, rest, pause) plus a heartbeat, and
// clears it when the player is left. The server forgets silent entries after
// ~2.5 min, so a closed tab or locked phone needs no cleanup.
export const useLiveWorkout = ({ planId, dayNumber, phase, exIdx, setIdx, queue, running, getElapsed }) => {
  const snapshot = useRef(null);
  const isLive = LIVE_PHASES.includes(phase) && queue.length > 0;

  snapshot.current = isLive ? {
    plan_id: planId,
    day_number: dayNumber,
    phase,
    exercise_name: queue[exIdx]?.name || null,
    exercise_index: exIdx,
    exercises_total: queue.length,
    set_index: setIdx,
    sets_total: queue[exIdx]?.sets || 0,
    paused: !running,
  } : null;

  // Every step change is reported immediately.
  useEffect(() => {
    if (!snapshot.current) return;
    send('POST', { ...snapshot.current, elapsed_seconds: Math.round(getElapsed() / 1000) });
  }, [isLive, phase, exIdx, setIdx, running]); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat keeps the entry fresh during long sets and rests.
  useEffect(() => {
    if (!isLive) return undefined;
    const id = setInterval(() => {
      if (snapshot.current) send('POST', { ...snapshot.current, elapsed_seconds: Math.round(getElapsed() / 1000) });
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [isLive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Back at the intro screen or leaving the player: no longer training.
  const wasLive = useRef(false);
  useEffect(() => {
    if (wasLive.current && !isLive) send('DELETE');
    wasLive.current = isLive;
  }, [isLive]);
  useEffect(() => () => { if (wasLive.current) send('DELETE'); }, []);
};
